import { useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/components/Navbar";
import { Flame, CalendarDays } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1475869430886-fb14585f7443?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxiYnElMjBzcHJlYWQlMjBtZWF0JTIwc2tld2VycyUyMGNvcm4lMjBjb2xvcmZ1bCUyMGZlYXN0fGVufDB8fHx8MTc5MjY5NDM0OXww&ixlib=rb-4.1.0&q=85&w=2400";

function nextEventDates() {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (out.length < 2) {
    if ([3, 0].includes(d.getDay())) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export default function Hero() {
  const { t, locale } = useLanguage();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 180]);
  const dates = useMemo(nextEventDates, []);
  const lines = [t.hero.l1, t.hero.l2, t.hero.l3];

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-svh flex items-end overflow-hidden">
      <motion.img src={HERO_IMG} alt="" style={{ y }}
        className="absolute inset-0 w-full h-[120%] object-cover opacity-70" />
      <div className="absolute inset-0 bg-gradient-to-t from-night via-night/55 to-night/25" />
      <div className="absolute inset-0 bg-gradient-to-r from-night/75 via-transparent to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-sunset/25 via-transparent to-transparent mix-blend-overlay pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[36rem] h-[36rem] rounded-full bg-sunset/25 blur-[140px] pointer-events-none" />
      <div className="absolute top-24 right-0 w-[26rem] h-[26rem] rounded-full bg-ocean/20 blur-[130px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-40 w-full">
        <motion.img src="/assets/logo.webp" alt="Curaçao Beach Barbecue" data-testid="hero-logo"
          initial={{ scale: 0.5, opacity: 0, rotate: -8 }} animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ delay: 0.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="w-24 h-24 sm:w-28 sm:h-28 rounded-full ring-2 ring-sunset/70 object-cover shadow-[0_0_70px_rgba(255,94,26,0.5)] mb-7" />

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
          className="inline-flex items-center gap-2 rounded-full border border-sunset/50 bg-night/60 backdrop-blur px-5 py-2 mb-5" data-testid="hero-tag">
          <Flame size={15} className="text-sunset" />
          <span className="font-semibold text-sm sm:text-base tracking-wide">{t.hero.tag}</span>
        </motion.div>

        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.6 }}
          className="font-mono text-xs uppercase tracking-[0.3em] text-ocean mb-6" data-testid="hero-kicker">
          {t.hero.kicker}
        </motion.p>

        <h1 className="font-display font-extrabold leading-[0.95] tracking-tight text-[clamp(3rem,11.5vw,8rem)]">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${i === 2 ? "text-sunset italic" : i === 1 ? "text-stroke" : ""}`}>
                {line}
                {i < 2 && <span className="text-sunset">.</span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-6 max-w-xl text-sm sm:text-base text-slate-300 leading-relaxed" data-testid="hero-sub">
          {t.hero.sub}
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-9 flex flex-wrap items-center gap-4">
          <button onClick={() => scrollToId("booking")} data-testid="hero-book-now-button"
            className="group flex items-center gap-2.5 bg-sunset text-night font-bold px-7 py-4 rounded-full text-sm sm:text-base hover:bg-flame hover:scale-[1.03] active:scale-95 transition-all duration-300 shadow-[0_0_40px_rgba(255,94,26,0.35)]">
            <Flame size={18} className="group-hover:animate-pulse" /> {t.hero.cta}
          </button>
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center rounded-full border border-sand/40 bg-sand/10 text-sand px-4 py-1.5 text-xs font-mono tracking-wider" data-testid="hero-price-badge">
              {t.hero.badge}
            </span>
            <span className="text-[11px] font-mono uppercase tracking-widest text-slate-400 pl-1">{t.hero.cap}</span>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-10 flex flex-wrap gap-3" data-testid="hero-next-dates">
          <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500 self-center">{t.hero.next}</span>
          {dates.map((d, i) => (
            <span key={i} className="inline-flex items-center gap-2 rounded-full border border-stroke bg-surface/70 backdrop-blur px-4 py-1.5 text-xs text-slate-200">
              <CalendarDays size={13} className="text-ocean" />
              {new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(d)}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
